import isArray from 'lodash/isArray';
import flatten from 'lodash/flatten';

import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {TrackingEvent as DataGroupSelectorTrackingEvent} from 'common/widgets/data-group-selector/util/tracking';
import {FlowsSankeyResponse} from 'common/report/funnels/models/api-types';

export const REPORT_NAME = `flows`;

/**
 * Definition of tracked events in Project 3 and the Flows project
 */
export const TrackingEvent = {
  EDIT_SESSION_TIMEOUT: `Edit session timeout`,
  SELECT_DATE_RANGE: `Select date range`,

  ADD_BREAK_DOWN: `Add event/property breakdown`,
  EDIT_BREAK_DOWN: `Edit event/property breakdown`,
  REMOVE_BREAK_DOWN: `Remove event/property breakdown`,

  EDIT_COHORT_FILTER: `Edit cohort filter`,
  EDIT_COHORT_FILTER_SEGMENT: `Edit cohort filter segment`,

  UPDATE_HIDDEN_EVENTS: `Hide events`,
  HIDE_EVENT: `Hide event`,

  CHANGE_FLOW_DIRECTION: `Change flow direction`,

  SELECT_DATA_GROUP: DataGroupSelectorTrackingEvent.GroupSelected,

  ADD_STEPS: `Add steps`,
  REMOVE_STEPS: `Remove steps`,
  CARDINALITY_CONTROL: `Cardinality control`,
  COLLAPSE_REPEATED: `Collapse repeated`,
  EXPAND_REPEATED: `Expand repeated`,

  RETURN_TO_FUNNEL: `DA Back to funnel clicked`,

  CHANGE_GLOBAL_ALIGNMENT: `Change global alignment`,

  ANY_ORDER_TOGGLE: `Any order toggle`,

  VIEW_AS_FUNNEL: `View as funnel`,
};

/**
 * Definition of tracked events in Project 3
 */
export const ProductionTrackingEvents = new Set([
  TrackingEvent.EDIT_SESSION_TIMEOUT,

  TrackingEvent.ADD_BREAK_DOWN,
  TrackingEvent.EDIT_BREAK_DOWN,
  TrackingEvent.REMOVE_BREAK_DOWN,

  TrackingEvent.EDIT_COHORT_FILTER,
  TrackingEvent.EDIT_COHORT_FILTER_SEGMENT,

  TrackingEvent.SELECT_DATE_RANGE,

  TrackingEvent.UPDATE_HIDDEN_EVENTS,
  TrackingEvent.HIDE_EVENT,

  TrackingEvent.CHANGE_FLOW_DIRECTION,
  TrackingEvent.SELECT_DATA_GROUP,

  TrackingEvent.ADD_STEPS,
  TrackingEvent.REMOVE_STEPS,
  TrackingEvent.REMOVE_STEPS,
  TrackingEvent.CARDINALITY_CONTROL,
  TrackingEvent.COLLAPSE_REPEATED,
  TrackingEvent.EXPAND_REPEATED,

  TrackingEvent.RETURN_TO_FUNNEL,

  TrackingEvent.CHANGE_GLOBAL_ALIGNMENT,
  TrackingEvent.ANY_ORDER_TOGGLE,
]);

export function getDataGroupTrackingProperties(report) {
  return {
    [commonTrackingEventPropertyName.dataGroupId]: report.dataGroupId,
    [commonTrackingEventPropertyName.filterByGroupProperty]: report.filters.some(
      (filterClause) => filterClause.dataGroupId,
    ),
    [commonTrackingEventPropertyName.segmentByGroupProperty]: false, // Flows only supports segmenting by Event properties
    [commonTrackingEventPropertyName.reportName]: REPORT_NAME,
  };
}

export function getLookupTableTrackingProperties(report) {
  const isLookupTableProperty = (prop) => {
    const value = prop.propertyName || prop.value;
    return !!prop.dataGroupId && isArray(value);
  };
  const countElements = (arr, func) => arr.filter(func).length;

  const countLookupPropertyFilters = (filters) => (filters ? countElements(filters, isLookupTableProperty) : 0);

  const numberOfLookupEventPropertyFilters = report._events
    ? report._events.reduce((acc, entry) => acc + countLookupPropertyFilters(entry.filters), 0)
    : 0;
  const numberOfLookupGlobalPropertyFilters = countLookupPropertyFilters(report.filters);
  const numberOfLookupPropertyFilters = numberOfLookupEventPropertyFilters + numberOfLookupGlobalPropertyFilters;
  const numberOfLookupPropertyExpandedEvents = report.segments
    ? countElements(report.segments, (segment) => isLookupTableProperty(segment.property))
    : 0;

  return {
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyFilters]: numberOfLookupPropertyFilters,
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyExpandedEvents]: numberOfLookupPropertyExpandedEvents,
    [commonTrackingEventPropertyName.usedLookupTableProperties]:
      numberOfLookupPropertyFilters + numberOfLookupPropertyExpandedEvents > 0,
  };
}

/**
 * Find the total number of nodes which correspond to a custom event.
 */
export function getNumCustomEvents(response: FlowsSankeyResponse) {
  const nodes = flatten(response.steps.map((step) => step.nodes));
  return nodes.filter((node) => node.isCustomEvent).length;
}
